import React from 'react';
import { Link } from 'react-router-dom';
import { useMarket } from '../../context/MarketContext';
import { Seo } from '../../components/Seo';
import { keywordsServices } from '../../seo/keywords';

interface Service {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  href?: string;
}

const services: Service[] = [
  {
    id: 'ecommerce-website',
    name: 'E-commerce Website Development',
    description: 'A standalone online store for your brand—catalog, cart, checkout, payments, and order management—built as an independent e-commerce platform.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ),
    features: ['Custom storefront', 'Catalog & checkout', 'Payment integration', 'Order management', 'Launch & support'],
    href: '/services/e-commerce',
  },
  {
    id: 'back-office',
    name: 'Back-Office Data Management',
    description: 'Our team receives your invoices and order documents by email (PDFs included), enters all data into your CimmpleFlow and/or CimmplePay subscription, so you can focus on core operations.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    features: ['Invoices & orders via email/PDF', 'Data entry for CimmpleFlow and/or CimmplePay', 'No data entry for you', 'Focus on core operations', 'Custom scope per subscription'],
    href: '/services/back-office',
  },
  {
    id: 'web-development',
    name: 'Web Development',
    description: 'Professional websites for machine shops and manufacturing businesses—showcase capabilities, build credibility, and make it easy for customers to get in touch.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    features: ['Design & development', 'Domain & hosting', 'RFQ & contact forms', 'Responsive on all devices', 'Ongoing support'],
    href: '/services/web-development',
  },
  {
    id: 'custom-software',
    name: 'Custom Software Development',
    description: 'Tailored software solutions designed specifically for your business needs. We build scalable, secure, and efficient applications from the ground up.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    features: ['Requirements Analysis', 'Architecture Design', 'Agile Development', 'Quality Assurance'],
  },
  {
    id: 'mobile-development',
    name: 'Mobile App Development',
    description: 'Native and cross-platform mobile applications for iOS and Android that deliver exceptional user experiences.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    features: ['iOS & Android', 'React Native', 'Flutter', 'App Store Deployment'],
  },
  {
    id: 'cloud-solutions',
    name: 'Cloud Solutions',
    description: 'Cloud architecture, migration, and management services. We help you leverage the power of AWS, Azure, and Google Cloud.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
    features: ['Cloud Migration', 'Infrastructure Setup', 'DevOps & CI/CD', 'Cost Optimization'],
  },
  {
    id: 'ui-ux-design',
    name: 'UI/UX Design',
    description: 'User-centered design that combines aesthetics with functionality. We create intuitive interfaces that users love.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    ),
    features: ['User Research', 'Wireframing', 'Prototyping', 'Design Systems'],
  },
  {
    id: 'consulting',
    name: 'IT Consulting',
    description: 'Strategic technology consulting to help you make informed decisions and optimize your IT investments.',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    features: ['Technology Assessment', 'Digital Strategy', 'Process Optimization', 'Vendor Selection'],
  },
];

const Services: React.FC = () => {
  const { market } = useMarket();
  const visibleServices = market === 'IN'
    ? services.filter((service) => service.id !== 'back-office')
    : services;

  return (
    <div className="pt-24">
      <Seo
        title="Custom Software Development Services | Cimmple"
        description="Custom software development, e-commerce websites, web apps, and back-office data management from Cimmple."
        keywords={[
          ...keywordsServices,
          'custom software company',
          'hire web developers',
        ]}
      />
      {/* Hero Section */}
      <section className="pt-8 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1 rounded-full bg-primary-100 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 text-sm font-medium mb-3">
            Our Services
          </span>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-3">
            Software development
            <br />
            <span className="gradient-text">expertise you can trust</span>
          </h1>
          <p className="text-slate-700 dark:text-dark-300 max-w-2xl mx-auto">
            From custom software development to cloud solutions, we provide end-to-end 
            services to bring your digital vision to life.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {visibleServices.map((service) => {
              const cardContent = (
                <>
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-2xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-primary-200 dark:group-hover:bg-primary-500/20 transition-all duration-300">
                    {service.icon}
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-slate-600 dark:text-dark-400 mb-6">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-700 dark:text-dark-300">
                        <svg className="w-4 h-4 text-primary-600 dark:text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </div>
                    ))}
                  </div>
                  {service.href && (
                    <div className="mt-4 flex items-center gap-2 font-semibold text-primary-600 dark:text-primary-400 group-hover:gap-4 transition-all duration-300">
                      Learn more
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  )}
                </>
              );
              return service.href ? (
                <Link
                  key={service.id}
                  to={service.href}
                  className="card group hover:border-primary-500/30 hover:shadow-xl hover:shadow-primary-500/5 transition-all duration-300 block"
                >
                  {cardContent}
                </Link>
              ) : (
                <div
                  key={service.id}
                  className="card group hover:border-primary-500/30 hover:shadow-xl hover:shadow-primary-500/5 transition-all duration-300"
                >
                  {cardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              Our <span className="gradient-text">Process</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              A proven methodology that ensures project success.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'We understand your business, goals, and requirements through in-depth consultation.' },
              { step: '02', title: 'Planning', description: 'Detailed project roadmap, architecture design, and resource allocation.' },
              { step: '03', title: 'Development', description: 'Agile development with regular updates, testing, and quality assurance.' },
              { step: '04', title: 'Delivery', description: 'Deployment, training, and ongoing support to ensure success.' },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-display font-bold text-primary-500/20 mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600 dark:text-dark-400 text-sm">
                  {item.description}
                </p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 right-0 w-1/2 h-0.5 bg-gradient-to-r from-primary-500/50 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
                Ready to start your project?
              </h2>
              <p className="text-slate-700 dark:text-dark-300 text-lg mb-8 max-w-xl mx-auto">
                Let's discuss how we can help you achieve your business goals with our software development expertise.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/contact" className="btn-primary">
                  Get in Touch
                </Link>
                <Link to="/products" className="btn-secondary">
                  View Our Products
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
